import { type CollectibleEventListRefined, useIntl, useSettings } from '@capsicum/common';
import { CardCarousel, Timer, useRouter } from '@capsicum/common-web';
import { useAuctions } from '@capsicum/features/fanshop/shared';
import { HeaderWarning } from '@capsicum/features/regulations/web';
import { useCallback, useMemo, useRef } from 'react';
import styled from 'styled-components';

import { useAuctionsTutorial } from '../../hooks';
import { AuctionCard, LoadingIndicator, SkeletonAuctions } from '../../ui';

type AuctionsProps = {
	partnerIds?: Array<string>;
};

export const Auctions = ({ partnerIds }: AuctionsProps) => {
	const { $t } = useIntl();
	const router = useRouter();
	const { settings } = useSettings();

	const { events, getAwayTeam, getHomeTeam, isEventClosed, isFetching, isLoading } = useAuctions({
		// We are setting 6 to decide whether to display See All button on 5 items carousel
		auctionsPerEvent: 6,
		partnerIds
	});

	const { firstAuctionCardRef } = useAuctionsTutorial(events);

	const listEndRef = useRef<HTMLDivElement>(null);

	const EventTitle = (event: CollectibleEventListRefined & { showSeeAllButton?: boolean }) => {
		const homeTeam = getHomeTeam(event);
		const awayTeam = getAwayTeam(event);

		return (
			<S.TitleWrapper>
				<S.TitleTextWrapper>
					<div>
						{homeTeam && <S.Title>{`${homeTeam} -`}</S.Title>}
						{awayTeam && <S.Subtitle>{awayTeam}</S.Subtitle>}
					</div>
					{isEventClosed(event.auctions!) && (
						<S.StyledTimer>
							<Timer closedLabel expirationDate={undefined} size="condensed" />
						</S.StyledTimer>
					)}
				</S.TitleTextWrapper>
				{event?.showSeeAllButton ? (
					<S.SeeAllButton
						data-testid={`event-see-all-${event.id}`}
						onClick={() => router.push(`/fan-shop/rewards/event/${event.id}`)}
					>
						{$t('global.seeAll')}
					</S.SeeAllButton>
				) : null}
			</S.TitleWrapper>
		);
	};

	const auctionsMemo = useCallback(
		(event: CollectibleEventListRefined, listIndex: number) =>
			event.auctions
				?.slice(0, 5)
				.map((auction, index) => (
					<AuctionCard
						auction={auction}
						key={auction.id}
						ref={
							settings.areTutorialsEnabled.auctionsLobby && listIndex === 0 && index === 0
								? firstAuctionCardRef
								: undefined
						}
						testID={`auction-card-${auction.id}`}
						onClick={() => router.push(`/fan-shop/rewards/auction/${auction.id}`)}
					/>
				)),
		[firstAuctionCardRef, router, settings.areTutorialsEnabled.auctionsLobby]
	);

	const eventsMemo = useMemo(
		() =>
			events &&
			events?.map((event, index) => (
				<div key={event.id}>
					<EventTitle {...event} />
					<S.StyledCardCarousel cardWidth={173} testID={`event-${event.id}`} variant="fullWidth">
						{auctionsMemo(event, index)}
					</S.StyledCardCarousel>
				</div>
			)),
		[auctionsMemo, events]
	);

	return (
		<>
			<S.AuctionsHeaderWarning page="rewards" />
			<S.PageWrapper>
				{isLoading ? <SkeletonAuctions /> : eventsMemo}
				<div ref={listEndRef}></div>
				{!isLoading && isFetching && <LoadingIndicator />}
			</S.PageWrapper>
		</>
	);
};

namespace S {
	export const PageWrapper = styled.div`
		${({ theme }) => theme.breakpoints.queries.only.mobile} {
			padding: ${({ theme }) => `${theme.spacings.small}`};
		}

		${({ theme }) => theme.breakpoints.queries.above.mobile} {
			padding: ${({ theme }) => `${theme.spacings.medium}`};
		}

		${({ theme }) => theme.breakpoints.queries.above.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.xxlarge}`};
		}
	`;

	export const Loading = styled.div`
		margin-top: ${({ theme }) => theme.spacings.xlarge};
		display: flex;
		align-self: center;
		justify-content: center;
	`;

	export const StyledTimer = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.medium};
		margin-left: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const StyledCardCarousel = styled(CardCarousel)`
		margin-bottom: ${({ theme }) => theme.spacings.xlarge};
	`;

	export const Title = styled.div`
		${({ theme }) => theme.fonts.headings.title5};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const TitleWrapper = styled.div`
		margin-bottom: ${({ theme }) => theme.spacings.xsmall};
		display: flex;
		justify-content: space-between;
		align-items: center;
	`;

	export const TitleTextWrapper = styled.div`
		display: flex;
		align-items: center;
	`;

	export const Subtitle = styled.div`
		${({ theme }) => theme.fonts.headings.title6};
		color: ${({ theme }) => theme.colors.text.main.unselected};
	`;

	export const SeeAllButton = styled.button`
		${({ theme }) => theme.fonts.text.text1};
		margin: ${({ theme }) => `${theme.spacings.xxxsmall} 0 0 ${theme.spacings.small}`};
		padding: 0;
		color: ${({ theme }) => theme.colors.text.brand.enabled};
		text-decoration: none;
		border: none;
		background: ${({ theme }) => theme.colors.backgrounds.transparent};
		flex-shrink: 0;
	`;

	export const AuctionsHeaderWarning = styled(HeaderWarning)`
		padding-top: ${({ theme }) => theme.spacings.small};
		top: 125px;
	`;
}
